import { NavLink } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { useOrgContext } from "../../context/OrgContext";
import { useLocale } from "../../context/LanguageContext";
import { useTheme } from "../../context/ThemeContext";
import type { Locale } from "../../i18n";
import { useOrganizationOrderStats } from "../../features/marketplace";
import { useLeadStats } from "../../features/leads";
import {
  LayoutDashboard,
  Users,
  Gift,
  ShoppingCart,
  Trophy,
  CalendarDays,
  Building2,
  Settings,
  UserSearch,
  Gauge,
  BarChart3,
  Bell,
  Layers,
  UserPlus,
  Activity,
  FileText,
  FileBarChart2,
  Store,
  ChevronLeft,
  ChevronRight,
  X,
  ShieldCheck,
} from "lucide-react";
import type { UserRole } from "../../types";

interface NavItem {
  labelKey: string;
  /** Path relative to org root — will be prefixed with /:orgSlug/ */
  path: string;
  icon: React.ElementType;
  /** Roles that can see this item */
  roles: UserRole[];
  /** If true, this is a global admin route (not org-scoped) */
  isGlobal?: boolean;
  /** Optional Vite env flag — item is hidden unless flag === "true". */
  featureFlag?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    labelKey: "nav.dashboard",
    path: "",
    icon: LayoutDashboard,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.settings",
    path: "settings",
    icon: Settings,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.roster",
    path: "roster",
    icon: Users,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.rewards",
    path: "rewards",
    icon: Gift,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.orders",
    path: "orders",
    icon: ShoppingCart,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.challenges",
    path: "challenges",
    icon: Trophy,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.events",
    path: "events",
    icon: CalendarDays,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.reports",
    path: "reports",
    icon: FileBarChart2,
    roles: ["ORG_ADMIN", "ADMIN"],
  },
  {
    labelKey: "nav.sso",
    path: "sso",
    icon: ShieldCheck,
    roles: ["ORG_ADMIN", "ADMIN"],
    featureFlag: "VITE_FEATURE_SSO_OIDC",
  },
  // ADMIN-only global items
  {
    labelKey: "nav.organizations",
    path: "/admin/organizations",
    icon: Building2,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.userLookup",
    path: "/admin/users",
    icon: UserSearch,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.economy",
    path: "/admin/economy",
    icon: Gauge,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.rewardCatalog",
    path: "/admin/rewards",
    icon: Store,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.analytics",
    path: "/admin/analytics",
    icon: BarChart3,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.notifications",
    path: "/admin/notifications",
    icon: Bell,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.queues",
    path: "/admin/queues",
    icon: Layers,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.leads",
    path: "/admin/leads",
    icon: UserPlus,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.healthSync",
    path: "/admin/health-sync",
    icon: Activity,
    roles: ["ADMIN"],
    isGlobal: true,
  },
  {
    labelKey: "nav.notificationLogs",
    path: "/admin/notification-logs",
    icon: FileText,
    roles: ["ADMIN"],
    isGlobal: true,
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { user } = useAuth();
  const { currentOrgId, currentOrg } = useOrgContext();
  const { t, locale, setLocale } = useLocale();
  const { resolvedTheme } = useTheme();

  // Fetch pending orders count for the sidebar badge (org-scoped)
  const { data: statsData } = useOrganizationOrderStats(currentOrgId ?? "");
  const pendingCount = statsData?.organizationOrderStats?.pendingCount ?? 0;

  // Fetch new lead count for the sidebar badge (ADMIN-only)
  const { data: leadStatsData } = useLeadStats();
  const newLeadCount =
    leadStatsData?.adminLeadStats?.byStatus?.find(
      (s) => s.status === "NEW",
    )?.count ?? 0;

  const orgPrefix = currentOrg?.slug ? `/${currentOrg.slug}` : "";

  const badgeMap: Record<string, number> = {
    orders: pendingCount,
    "/admin/leads": newLeadCount,
  };

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!user || !item.roles.includes(user.role)) return false;
    if (item.featureFlag) {
      const env = import.meta.env as Record<string, string | undefined>;
      if (env[item.featureFlag] !== "true") return false;
    }
    return true;
  });

  // Separate org-scoped and global admin sections
  const orgItems = visibleItems.filter((item) => !item.isGlobal);
  const adminItems = visibleItems.filter((item) => item.isGlobal);

  /** Build full path: org items get prefixed with /:orgSlug/, global items use absolute path */
  const getFullPath = (item: NavItem) => {
    if (item.isGlobal) return item.path;
    return item.path === "" ? orgPrefix || "/" : `${orgPrefix}/${item.path}`;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 touch-none lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`flex h-dvh flex-col border-r border-sidebar-border bg-sidebar transition-[width,transform] duration-200 fixed inset-y-0 left-0 z-50 w-60 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:static lg:z-auto lg:translate-x-0 ${
          collapsed ? "lg:w-16" : "lg:w-60"
        }`}
      >
        {/* Logo / Brand */}
        <div className="flex h-14 items-center border-b border-sidebar-border px-4 md:h-16">
          {/* Mobile: always show full brand */}
          <span className="text-lg font-bold text-primary lg:hidden">Kommit</span>
          {/* Desktop: toggle based on collapsed */}
          {!collapsed && (
            <span className="hidden text-lg font-bold text-primary lg:inline">Kommit</span>
          )}
          {collapsed && (
            <span className="mx-auto hidden text-lg font-bold text-primary lg:block">K</span>
          )}
          {/* Mobile close button */}
          <button
            type="button"
            onClick={onMobileClose}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground lg:hidden"
            aria-label={t("nav.closeMenu")}
          >
            <X size={18} />
          </button>
        </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overscroll-contain px-2 py-4">
        <ul className="space-y-1">
          {orgItems.map((item) => (
            <SidebarItem
              key={item.path}
              item={item}
              fullPath={getFullPath(item)}
              collapsed={collapsed}
              badge={badgeMap[item.path] || 0}
              t={t}
            />
          ))}
        </ul>

        {adminItems.length > 0 && (
          <>
            <div className="my-4 border-t border-sidebar-border" />
            {!collapsed && (
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("nav.adminSection")}
              </p>
            )}
            <ul className="space-y-1">
              {adminItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  item={item}
                  fullPath={getFullPath(item)}
                  collapsed={collapsed}
                  badge={badgeMap[item.path] || 0}
                  t={t}
                />
              ))}
            </ul>
          </>
        )}

        {/* Mobile language toggle — inside scrollable area so it's not hidden behind browser chrome */}
        <div className="mt-4 border-t border-sidebar-border px-1 pt-4 lg:hidden">
          <MobileLangToggle locale={locale} setLocale={setLocale} t={t} resolvedTheme={resolvedTheme} />
        </div>
      </nav>

      {/* Collapse toggle — desktop only */}
      <button
        type="button"
        onClick={onToggle}
        className="hidden h-12 items-center justify-center border-t border-sidebar-border text-muted-foreground hover:text-foreground lg:flex"
        aria-label={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
      >
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>
    </aside>
    </>
  );
}

function SidebarItem({
  item,
  fullPath,
  collapsed,
  badge,
  t,
}: {
  item: NavItem;
  fullPath: string;
  collapsed: boolean;
  badge: number;
  t: (key: string) => string;
}) {
  const label = t(item.labelKey);
  return (
    <li>
      <NavLink
        to={fullPath}
        end={item.path === ""}
        className={({ isActive }) =>
          `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            isActive
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
          } ${collapsed ? "justify-center" : ""}`
        }
        title={collapsed ? `${label}${badge > 0 ? ` (${badge})` : ""}` : undefined}
      >
        <item.icon size={20} />
        {!collapsed && (
          <>
            <span className="flex-1">{label}</span>
            {badge > 0 && (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold leading-none text-destructive-foreground">
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </>
        )}
        {collapsed && badge > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </NavLink>
    </li>
  );
}

/* ─── Mobile Language Toggle (sidebar footer) ─── */

const LOCALE_OPTIONS: { value: Locale; label: string; ariaKey: string }[] = [
  { value: "de", label: "DE", ariaKey: "topbar.switchTo.de" },
  { value: "en", label: "EN", ariaKey: "topbar.switchTo.en" },
  { value: "tr", label: "TR", ariaKey: "topbar.switchTo.tr" },
];

function MobileLangToggle({
  locale,
  setLocale,
  t,
  resolvedTheme,
}: {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
  resolvedTheme: "light" | "dark";
}) {
  const isLight = resolvedTheme === "light";
  return (
    <div
      className={`flex h-10 w-full items-center justify-center gap-1 rounded-2xl p-1 backdrop-blur-sm ${
        isLight
          ? "border border-border/80 bg-muted/70 shadow-sm"
          : "border border-white/15 bg-white/10 shadow-[0_2px_10px_rgba(2,6,23,0.25)]"
      }`}
      role="radiogroup"
      aria-label={t("topbar.language")}
    >
      {LOCALE_OPTIONS.map(({ value, label, ariaKey }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={locale === value}
          aria-label={t(ariaKey)}
          onClick={() => setLocale(value)}
          className={`flex h-8 w-11 items-center justify-center rounded-full ring-1 ring-transparent text-xs font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 ${
            isLight ? "focus-visible:ring-primary/35" : "focus-visible:ring-white/70"
          } ${
            locale === value
              ? isLight
                ? "bg-card text-foreground shadow-sm ring-border"
                : "bg-white text-foreground shadow-sm ring-white/70"
              : isLight
                ? "text-muted-foreground hover:bg-background hover:text-foreground"
                : "text-muted-foreground hover:bg-white/18 hover:text-foreground"
          }`}
          title={t(ariaKey)}
        >
          {value === "de" ? (
            <svg className="h-4.5 w-6 rounded-[2px]" viewBox="0 0 24 16" aria-hidden="true">
              <rect width="24" height="16" fill="#ffce00" />
              <rect width="24" height="10.67" fill="#dd0000" />
              <rect width="24" height="5.33" fill="#000000" />
            </svg>
          ) : value === "tr" ? (
            <svg className="h-4.5 w-6 rounded-[2px]" viewBox="0 0 24 16" aria-hidden="true">
              <rect width="24" height="16" fill="#e30a17" />
              <circle cx="10.5" cy="8" r="3.2" fill="#ffffff" />
              <circle cx="11.6" cy="8" r="2.6" fill="#e30a17" />
              <path
                d="M16.1 8 14.63 7.47 14.58 5.91 13.62 7.14 12.12 6.71 13.0 8 12.12 9.29 13.62 8.86 14.58 10.09 14.63 8.53Z"
                fill="#ffffff"
              />
            </svg>
          ) : (
            <svg className="h-4.5 w-6 rounded-[2px]" viewBox="0 0 24 16" aria-hidden="true">
              <rect width="24" height="16" fill="#0a47a1" />
              <path d="M0 0L24 16M24 0L0 16" stroke="#ffffff" strokeWidth="3.4" />
              <path d="M0 0L24 16M24 0L0 16" stroke="#cf142b" strokeWidth="1.8" />
              <path d="M12 0v16M0 8h24" stroke="#ffffff" strokeWidth="5.2" />
              <path d="M12 0v16M0 8h24" stroke="#cf142b" strokeWidth="3" />
            </svg>
          )}
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}
